import { WsStreamType } from '../constants/ws';
import {
  debugLog,
  StreamDataMap,
  StreamHandler,
  SubscribeParams,
  useWebsocketStore,
} from '../hooks/useWebsocketStore';
import { OrderFillsSchema } from '../types/fills';
import { SubaccountLiquidationSchema } from '../types/liquidation';
import { BookDepthSchema } from '../types/orderbook';
import { OrderUpdateSchema } from '../types/orders';
import { MarketPriceSchema } from '../types/prices';
import { TokenTransferSchema } from '../types/tokenTransfer';

export const getSubKey = (streamType: WsStreamType, params: SubscribeParams[WsStreamType]) =>
  `${streamType}:${JSON.stringify(params)}`;

export function subscribeToStream<T extends WsStreamType>(
  streamType: T,
  params: SubscribeParams[T],
  handler: StreamHandler<T>,
  // This is useful for stateful subscriptions like BookDepth
  allHandlersRemovedCb?: () => void
) {
  const state = useWebsocketStore.getState();
  const key = getSubKey(streamType, params);

  let subscription = state.subscriptions[streamType].get(key);

  if (!subscription) {
    // Create the specific parameters for this stream type
    subscription = {
      params,
      handlers: [],
    };
    state.subscriptions[streamType].set(key, subscription);

    // If connected, subscribe to server
    if (state.socket && state.isConnected) {
      debugLog(streamType, `Subscribing to ${streamType}`, params);
      state.socket.emit('subscribe', subscription.params);
    }
  }

  // Check if handler already exists
  if (!subscription.handlers.includes(handler)) {
    subscription.handlers.push(handler);
  }

  // Return unsubscribe function
  return () => {
    const currentState = useWebsocketStore.getState();
    const currentSubscription = currentState.subscriptions[streamType].get(key);

    if (!currentSubscription) return;

    // Remove handler
    const newHandlers = currentSubscription.handlers.filter((h) => h !== handler);
    const currentParams = currentSubscription.params;

    if (newHandlers.length === 0) {
      allHandlersRemovedCb?.();
      // No more handlers, remove subscription
      currentState.subscriptions[streamType].delete(key);

      // If connected, unsubscribe from server
      if (currentState.socket?.connected) {
        debugLog(streamType, `Unsubscribing from ${streamType}`, params);
        currentState.socket.emit('unsubscribe', currentParams);
      }
    } else {
      debugLog(
        streamType,
        `Unsubscribe called for ${streamType} but there are still handlers subscribed`
      );
      // Update handlers
      currentSubscription.handlers = newHandlers;
    }
  };
}

function routeMessageToHandlers<T extends WsStreamType>(
  streamType: T,
  parsedData: StreamDataMap[T],
  getSubscriptionKey?: (data: StreamDataMap[T]) => string | undefined
) {
  const { subscriptions } = useWebsocketStore.getState();
  const key = getSubscriptionKey?.(parsedData);
  const filteredSubscriptions = key
    ? [subscriptions[streamType].get(key)]
    : subscriptions[streamType];

  filteredSubscriptions.forEach((subscription) => {
    if (subscription) {
      debugLog(streamType, `Routing to ${subscription.handlers.length} handlers`);
      subscription.handlers.forEach((handler) => handler(parsedData));
    }
  });
}

// Type-specific message routing functions
export function routeBookDepthMessage(rawData: unknown) {
  const data = BookDepthSchema.parse(rawData);
  const state = useWebsocketStore.getState();
  const { subscriptions, bookDepthLastProcessedTime, socket } = state;
  const key = getSubKey(WsStreamType.BOOK_DEPTH, {
    type: WsStreamType.BOOK_DEPTH,
    productId: data.productId,
  });
  const subscription = subscriptions[WsStreamType.BOOK_DEPTH].get(key);

  if (subscription) {
    const storedPrevTimestamp = bookDepthLastProcessedTime[data.productId] || 0;

    // Check if we missed updates by comparing timestamps
    if (storedPrevTimestamp > 0 && data.previousTimestamp !== storedPrevTimestamp) {
      console.warn(
        WsStreamType.BOOK_DEPTH,
        `Timestamp mismatch detected. Stored: ${storedPrevTimestamp}, Received previous: ${data.previousTimestamp}`
      );

      // We missed updates, need to unsubscribe and resubscribe to get full orderbook
      if (socket?.connected) {
        // First unsubscribe
        socket.emit('unsubscribe', subscription.params);
        useWebsocketStore.setState((state) => ({
          bookDepthLastProcessedTime: {
            ...state.bookDepthLastProcessedTime,
            [data.productId]: 0,
          },
        }));

        // This will reset the orderbook to an empty state.
        const firstHandler = subscription.handlers.at(0);
        if (firstHandler) {
          firstHandler(null);
        }

        // Then resubscribe to get the full orderbook
        socket.emit('subscribe', subscription.params);

        return; // Don't process this update
      }
    }

    // Process the update normally
    debugLog(WsStreamType.BOOK_DEPTH, `Routing to ${subscription.handlers.length} handlers`);
    useWebsocketStore.setState((state) => ({
      bookDepthLastProcessedTime: {
        ...state.bookDepthLastProcessedTime,
        [data.productId]: data.timestamp,
      },
    }));
    subscription.handlers.forEach((handler) => handler(data));
  }
}

// Fills does not have subaccountId, so we have to send the update to all subs.
export const routeOrderFillMessage = (rawData: unknown) =>
  routeMessageToHandlers(WsStreamType.ORDER_FILL, OrderFillsSchema.parse(rawData));

export const routeOrderUpdateMessage = (rawData: unknown) =>
  routeMessageToHandlers(WsStreamType.ORDER_UPDATE, OrderUpdateSchema.parse(rawData), (data) =>
    getSubKey(WsStreamType.ORDER_UPDATE, {
      type: WsStreamType.ORDER_UPDATE,
      subaccountId: data.subaccountId,
    })
  );

export const routeMarketPriceMessage = (rawData: unknown) =>
  routeMessageToHandlers(WsStreamType.MARKET_PRICE, MarketPriceSchema.parse(rawData), (data) =>
    getSubKey(WsStreamType.MARKET_PRICE, {
      type: WsStreamType.MARKET_PRICE,
      productId: data.productId,
    })
  );

export const routeSubaccountLiquidationMessage = (rawData: unknown) =>
  routeMessageToHandlers(
    WsStreamType.SUBACCOUNT_LIQUIDATION,
    SubaccountLiquidationSchema.parse(rawData),
    (data) =>
      getSubKey(WsStreamType.SUBACCOUNT_LIQUIDATION, {
        type: WsStreamType.SUBACCOUNT_LIQUIDATION,
        subaccountId: data.subaccountId,
      })
  );

export const routeTokenTransferMessage = (rawData: unknown) => {
  const data = TokenTransferSchema.parse(rawData);

  // All transfers in a message have the same subaccountId
  const subaccountId = data.data[0]?.subaccountId;
  if (!subaccountId) return;

  routeMessageToHandlers(WsStreamType.TOKEN_TRANSFER, data, () =>
    getSubKey(WsStreamType.TOKEN_TRANSFER, {
      type: WsStreamType.TOKEN_TRANSFER,
      subaccountId,
    })
  );
};
